import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { X } from 'lucide-react';

import { cn } from '@/lib/cn';
import { motionSafe, springSheet } from '@/lib/motion';

interface Props {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

/**
 * Side sheet on desktop, bottom sheet on mobile.
 *
 * A sheet rather than an inline expand because this is a deeper level of
 * detail, not more of the same row. It is modal, so it gets a scrim -- the
 * background is pushed back rather than left competing.
 */
export function Sheet({ title, onClose, children, footer }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  // The sheet docks to a different edge on small screens, so it has to enter
  // and leave along that edge too. Sliding in from the right onto a surface
  // that is anchored to the bottom reads as two unrelated things happening.
  const [compact, setCompact] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia('(max-width: 639px)');
    const sync = () => setCompact(query.matches);
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    // Remember where focus came from so closing puts it back. Dumping the
    // user at the top of the document is disorienting with a keyboard.
    returnFocusTo.current = document.activeElement as HTMLElement | null;

    const first = panelRef.current?.querySelector<HTMLElement>(
      'input, textarea, button, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prev;
      returnFocusTo.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      // Keep Tab inside the sheet while it is open.
      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-overlay flex justify-end" role="dialog" aria-modal="true" aria-label={title}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={motionSafe({ duration: 0.15 })}
        onClick={onClose}
        className="absolute inset-0 bg-scrim"
      />

      <motion.div
        ref={panelRef}
        // Enter and exit share one path, so the surface returns the way it
        // arrived: from the right on desktop, from the bottom on mobile.
        initial={compact ? { y: '100%', opacity: 0.6 } : { x: '100%', opacity: 0.6 }}
        animate={{ x: 0, y: 0, opacity: 1 }}
        exit={compact ? { y: '100%', opacity: 0.6 } : { x: '100%', opacity: 0.6 }}
        transition={motionSafe(springSheet)}
        className={cn(
          'relative flex h-dvh w-full max-w-md flex-col border-l border-border bg-surface-1 shadow-sheet',
          'max-sm:mt-auto max-sm:h-[88dvh] max-sm:max-w-none max-sm:rounded-t-xl max-sm:border-l-0 max-sm:border-t',
        )}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="truncate text-small font-medium text-text-secondary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="pressable flex size-action items-center justify-center rounded-full text-text-tertiary hover:bg-surface-3 hover:text-text"
          >
            <X size={16} strokeWidth={1.5} aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>

        {footer && (
          <footer className="shrink-0 border-t border-border px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </footer>
        )}
      </motion.div>
    </div>
  );
}
